import crawledCountries from "../../../lcr-crawler/data/crawled/countries.json";
import crawledRequirements from "../../../lcr-crawler/data/crawled/country_disease_requirements.json";
import { countryNamesBySlug } from "../content/travel/country-names.generated";
import { diseaseLcrUrl, diseaseName } from "../content/travel/diseases";
import type { Locale } from "../i18n/locales";

export const REGION_SLUGS = [
  "zuidoost-azie",
  "sub-sahara-afrika",
  "zuid-amerika",
  "midden-oosten",
  "europa-noord-amerika",
  "zuid-centraal-azie",
  "cariben",
  "oost-azie",
  "oceanie",
] as const;

export type RegionSlug = (typeof REGION_SLUGS)[number];

// ponytail: static NL-leisure ranking; upgrade path = analytics on /travel views
export const REGION_POPULARITY_ORDER = [
  "europa-noord-amerika",
  "cariben",
  "zuidoost-azie",
  "oost-azie",
  "midden-oosten",
  "zuid-amerika",
  "sub-sahara-afrika",
  "zuid-centraal-azie",
  "oceanie",
] as const satisfies readonly RegionSlug[];

const regionPopularityRank = new Map(
  REGION_POPULARITY_ORDER.map((slug, index) => [slug, index]),
);

export function sortRegionsByPopularity<T extends { slug: string }>(
  regions: readonly T[],
): T[] {
  return [...regions].sort(
    (a, b) =>
      (regionPopularityRank.get(a.slug as RegionSlug) ??
        REGION_POPULARITY_ORDER.length) -
      (regionPopularityRank.get(b.slug as RegionSlug) ??
        REGION_POPULARITY_ORDER.length),
  );
}

const REGION_COUNTRIES: Record<RegionSlug, readonly string[]> = {
  "zuidoost-azie": [
    "brunei-darussalam",
    "cambodja",
    "filipijnen",
    "indonesie",
    "laos",
    "maleisie",
    "myanmar",
    "oost-timor",
    "singapore",
    "thailand",
    "vietnam",
  ],
  "sub-sahara-afrika": [
    "angola",
    "benin",
    "botswana",
    "burkina-faso",
    "burundi",
    "centraal-afrikaanse-republiek",
    "comoren",
    "congo",
    "congo-dr",
    "djibouti",
    "equatoriaal-guinea",
    "eritrea",
    "ethiopie",
    "gabon",
    "gambia",
    "ghana",
    "guinee",
    "guinee-bissau",
    "ivoorkust",
    "kaapverdie",
    "kameroen",
    "kenia",
    "lesotho",
    "liberia",
    "madagaskar",
    "malawi",
    "mali",
    "mauritanie",
    "mauritius",
    "mayotte",
    "mozambique",
    "namibie",
    "niger",
    "nigeria",
    "reunion",
    "rwanda",
    "senegal",
    "seychellen",
    "sierra-leone",
    "somalie",
    "st-helena",
    "sudan",
    "swaziland",
    "tanzania",
    "togo",
    "tsjaad",
    "uganda",
    "zambia",
    "zimbabwe",
    "zuid-afrika",
    "zuid-sudan",
  ],
  "zuid-amerika": [
    "argentinie",
    "bolivia",
    "brazilie",
    "chili",
    "colombia",
    "ecuador",
    "falkland-eilanden",
    "frans-guyana",
    "guyana",
    "paraguay",
    "peru",
    "suriname",
    "uruguay",
    "venezuela",
  ],
  "midden-oosten": [
    "algerije",
    "egypte",
    "irak",
    "iran",
    "israel",
    "jemen",
    "jordanie",
    "koeweit",
    "libanon",
    "libie",
    "marokko",
    "palestijnse-gebieden",
    "qatar",
    "saoedi-arabie",
    "syrie",
    "tunesie",
    "turkije",
    "verenigde-arabische-emiraten",
    "westelijke-sahara",
  ],
  "europa-noord-amerika": [
    "albanie",
    "andorra",
    "armenie",
    "azerbeidzjan",
    "bermuda",
    "bosnie-en-herzegovina",
    "bulgarije",
    "canada",
    "denemarken",
    "duitsland",
    "estland",
    "finland",
    "frankrijk",
    "georgie",
    "groenland",
    "hawaii",
    "italie",
    "kosovo",
    "kroatie",
    "letland",
    "liechtenstein",
    "litouwen",
    "malta",
    "moldavie",
    "montenegro",
    "noord-macedonie",
    "noorwegen",
    "oekraine",
    "oostenrijk",
    "portugal",
    "roemenie",
    "rusland",
    "servie",
    "slovenie",
    "slowakije",
    "spanje",
    "spitsbergen",
    "tsjechie",
    "verenigde-staten-van-amerika",
    "wit-rusland",
    "zweden",
    "zwitserland",
  ],
  "zuid-centraal-azie": [
    "afghanistan",
    "bangladesh",
    "bhutan",
    "india",
    "kazachstan",
    "kirgistan",
    "maldiven",
    "nepal",
    "oezbekistan",
    "pakistan",
    "sri-lanka",
    "tadzjikistan",
    "turkmenistan",
  ],
  cariben: [
    "amerikaanse-maagdeneilanden",
    "anguilla",
    "antigua-en-barbuda",
    "aruba",
    "bahamas",
    "barbados",
    "belize",
    "bonaire",
    "britse-maagdeneilanden",
    "costa-rica",
    "cuba",
    "curacao",
    "dominica",
    "dominicaanse-republiek",
    "el-salvador",
    "franse-antillen",
    "grenada",
    "guatemala",
    "haiti",
    "honduras",
    "jamaica",
    "kaaiman-eilanden",
    "mexico",
    "montserrat",
    "nicaragua",
    "panama",
    "puerto-rico",
    "saba",
    "st-eustatius",
    "st-kitts-en-nevis",
    "st-lucia",
    "st-maarten",
    "st-vincent-en-de-grenadines",
    "trinidad-en-tobago",
    "turks-en-caicoseilanden",
  ],
  "oost-azie": [
    "china",
    "china-hongkong",
    "china-macau",
    "china-taiwan",
    "japan",
    "mongolie",
    "noord-korea",
    "zuid-korea",
  ],
  oceanie: [
    "amerikaans-samoa",
    "australie",
    "chagosarchipel",
    "christmaseiland",
    "cookeilanden",
    "coral-sea-eilanden",
    "frans-polynesie",
    "guam",
    "kiribati",
    "marshalleilanden",
    "micronesie",
    "nauru",
    "nieuw-caledonie",
    "nieuw-zeeland",
    "niue",
    "noordelijke-marianen",
    "norfolkeiland",
    "palau",
    "papoea-nieuw-guinea",
    "pitcairneilanden",
    "salomonseilanden",
    "samoa",
    "tokelau",
    "tonga",
    "tuvalu",
    "vanuatu",
    "wake-island",
    "wallis-en-futuna",
  ],
};

const slugToRegion = Object.fromEntries(
  Object.entries(REGION_COUNTRIES).flatMap(([region, slugs]) =>
    slugs.map((slug) => [slug, region as RegionSlug]),
  ),
) as Record<string, RegionSlug>;

type CrawledCountry = {
  slug: string;
  name_nl: string;
  name_en: string;
  iso: string | null;
  iso3: string | null;
  lcr_url: string;
  last_crawled: string;
};

type CrawledRequirement = {
  country_slug: string;
  disease_slug: string;
  source_section: "general" | "conditional" | "other";
  applies_to: string;
  last_crawled?: string;
  recommendation_text_nl: string;
  recommendation_text_en: string;
  recommendation_text_de: string;
  recommendation_text_fr: string;
  recommendation_text_es: string;
};

export type TravelRequirement = {
  disease: string;
  section: "general" | "conditional" | "other";
  appliesTo: string;
  text: Record<Locale, string>;
};

export type TravelCountry = {
  slug: string;
  nameNl: string;
  nameEn: string;
  iso: string | null;
  iso3: string | null;
  lcrUrl: string;
  lastCrawled: string;
  region: RegionSlug | null;
  requirements: TravelRequirement[];
};

const SECTION_ORDER = ["general", "conditional", "other"] as const;

function newerDate(a?: string, b?: string): string {
  if (!a) return b ?? "";
  if (!b) return a;
  return Date.parse(a) >= Date.parse(b) ? a : b;
}

function buildCountries(): TravelCountry[] {
  const byCountry = new Map<string, CrawledRequirement[]>();
  for (const row of crawledRequirements as CrawledRequirement[]) {
    const list = byCountry.get(row.country_slug) ?? [];
    list.push(row);
    byCountry.set(row.country_slug, list);
  }

  return (crawledCountries as CrawledCountry[])
    .filter((country) => byCountry.has(country.slug))
    .map((country) => {
      const rows = byCountry.get(country.slug) ?? [];
      const lastCrawled = rows.reduce(
        (latest, row) => newerDate(latest, row.last_crawled),
        country.last_crawled,
      );
      return {
        slug: country.slug,
        nameNl: country.name_nl,
        nameEn: country.name_en,
        iso: country.iso,
        iso3: country.iso3,
        lcrUrl: country.lcr_url,
        lastCrawled,
        region: slugToRegion[country.slug] ?? null,
        requirements: rows.map((row) => ({
          disease: row.disease_slug,
          section: row.source_section,
          appliesTo: row.applies_to,
          text: {
            nl: row.recommendation_text_nl,
            en: row.recommendation_text_en,
            de: row.recommendation_text_de,
            fr: row.recommendation_text_fr,
            es: row.recommendation_text_es,
          },
        })),
      };
    })
    .sort((a, b) => a.slug.localeCompare(b.slug));
}

const countries = buildCountries();
const bySlug = new Map(countries.map((country) => [country.slug, country]));

export function isRegionSlug(value: string): value is RegionSlug {
  return (REGION_SLUGS as readonly string[]).includes(value);
}

export function getCountry(slug: string): TravelCountry | undefined {
  return bySlug.get(slug);
}

export function countriesWithAdvice(): TravelCountry[] {
  return countries;
}

export function countriesInRegion(region: RegionSlug): TravelCountry[] {
  return countries.filter((country) => country.region === region);
}

export function travelSlugs(): string[] {
  return [...REGION_SLUGS, ...countries.map((country) => country.slug)];
}

export type DiseaseCountryHit = {
  country: TravelCountry;
  section: TravelRequirement["section"];
  text: TravelRequirement["text"];
};

export type TravelDisease = {
  slug: string;
  lcrUrl?: string;
  lastCrawled: string;
  countries: DiseaseCountryHit[];
};

function buildDiseases(): TravelDisease[] {
  const byDisease = new Map<string, DiseaseCountryHit[]>();
  const lastByDisease = new Map<string, string>();

  for (const country of countries) {
    for (const item of country.requirements) {
      const hits = byDisease.get(item.disease) ?? [];
      hits.push({ country, section: item.section, text: item.text });
      byDisease.set(item.disease, hits);
    }
  }

  for (const row of crawledRequirements as CrawledRequirement[]) {
    lastByDisease.set(
      row.disease_slug,
      newerDate(lastByDisease.get(row.disease_slug), row.last_crawled),
    );
  }

  return [...byDisease.entries()]
    .map(([slug, hits]) => ({
      slug,
      lcrUrl: diseaseLcrUrl(slug),
      lastCrawled:
        lastByDisease.get(slug) ?? hits[0]?.country.lastCrawled ?? "",
      countries: hits.sort((a, b) =>
        a.country.slug.localeCompare(b.country.slug),
      ),
    }))
    .sort((a, b) => a.slug.localeCompare(b.slug));
}

const diseases = buildDiseases();
const diseasesBySlug = new Map(diseases.map((item) => [item.slug, item]));

export function diseasesWithAdvice(): TravelDisease[] {
  return diseases;
}

export function getDisease(slug: string): TravelDisease | undefined {
  return diseasesBySlug.get(slug);
}

export function travelDiseasePaths(): string[] {
  return ["diseases", ...diseases.map((item) => `diseases/${item.slug}`)];
}

export function uniqueDiseaseCountryCount(disease: TravelDisease): number {
  return new Set(disease.countries.map((hit) => hit.country.slug)).size;
}

export function diseaseCountriesBySection(disease: TravelDisease) {
  return SECTION_ORDER.map((section) => ({
    section,
    countries: disease.countries
      .filter((hit) => hit.section === section)
      .map((hit) => hit.country)
      .filter(
        (country, index, list) =>
          list.findIndex((item) => item.slug === country.slug) === index,
      ),
  })).filter((group) => group.countries.length > 0);
}

export function diseaseLooksRequired(disease: TravelDisease): boolean {
  return disease.countries.some((hit) =>
    looksRequired(hit.text.nl || hit.text.en),
  );
}

export function countryName(country: TravelCountry, locale: Locale): string {
  return (
    countryNamesBySlug[country.slug]?.[locale] ??
    (locale === "nl" ? country.nameNl : country.nameEn)
  );
}

export function groupedRequirements(country: TravelCountry) {
  return SECTION_ORDER.map((section) => ({
    section,
    items: country.requirements.filter((item) => item.section === section),
  })).filter((group) => group.items.length > 0);
}

export function generalVaccineNames(
  country: TravelCountry,
  locale: Locale,
): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const item of country.requirements) {
    if (item.section !== "general") continue;
    if (seen.has(item.disease)) continue;
    seen.add(item.disease);
    names.push(diseaseName(item.disease, locale));
  }
  return names;
}

export function requiredVaccineNames(
  country: TravelCountry,
  locale: Locale,
): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const item of country.requirements) {
    if (!looksRequired(item.text[locale] || item.text.nl)) continue;
    if (seen.has(item.disease)) continue;
    seen.add(item.disease);
    names.push(diseaseName(item.disease, locale));
  }
  return names;
}

export function formatCrawlDate(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function fillTemplate(
  template: string,
  vars: Record<string, string>,
): string {
  return template.replaceAll(/\{(\w+)\}/g, (_, key: string) => vars[key] ?? "");
}

function looksRequired(text: string): boolean {
  return /verplicht|mandatory|required for entry|obligatoire|pflicht/i.test(
    text,
  );
}
